import React, { useState, useEffect } from 'react';
import { exploreService } from './exploreService';
import MapComponent from './MapComponent';
import ShareLocationComponent from './ShareLocationComponent';

const RADIUS_OPTIONS = [
  { value: 100, label: '100m' },
  { value: 500, label: '500m' },
  { value: 1000, label: '1km' },
  { value: 5000, label: '5km' },
  { value: 10000, label: '10km' },
  { value: 25000, label: '25km' },
  { value: 50000, label: '50km' },
];

const POPULAR_CITIES = [
  { name: 'Cairo', country: 'Egypt' },
  { name: 'New York', country: 'USA' },
  { name: 'London', country: 'UK' },
  { name: 'Paris', country: 'France' },
  { name: 'Tokyo', country: 'Japan' },
  { name: 'Dubai', country: 'UAE' },
  { name: 'Sydney', country: 'Australia' },
  { name: 'Oslo', country: 'Norway' },
  { name: 'Mumbai', country: 'India' },
  { name: 'Berlin', country: 'Germany' },
  { name: 'Toronto', country: 'Canada' },
];

const ExploreComponent = () => {
  // State for categories and places
  const [categories, setCategories] = useState([]);
  const [places, setPlaces] = useState([]);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showMap, setShowMap] = useState(false);
const [selectedPlace, setSelectedPlace] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    latitude: '',
    longitude: '',
    city: '',
    country: '',
    radius: 1000,
    category: 'all',
    limit: 10,
    query: '',
  });
  
  // Location info
  const [locationInfo, setLocationInfo] = useState(null);
  
  // Search type: 'coordinates' | 'city'
  const [searchType, setSearchType] = useState('city');
  
  // UI state
  const [showCategoryGrid, setShowCategoryGrid] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Initialize - fetch categories
  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch categories from API
      const cats = await exploreService.getExploreCategories();
      setCategories(cats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'radius' || name === 'limit' ? parseInt(value) || '' : value,
    }));
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({
      ...prev,
      category: category.id,
    }));
    setShowCategoryGrid(false);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setShowCategoryGrid(true);
    setFormData(prev => ({
      ...prev,
      category: 'all',
    }));
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setError(null);
    setSuccess(null);
    
    if (type === 'coordinates') {
      setFormData(prev => ({
        ...prev,
        city: '',
        country: '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
      }));
    }
  };

  const handleSearchByCity = async () => {
    if (!formData.city.trim()) {
      setError('Please enter a city name');
      return;
    }

    setSearching(true);
    setError(null);
    setSuccess(null);

    try {
      // Get coordinates for the city
      const geoResult = await exploreService.getCityCoordinates(
        formData.city,
        formData.country || null
      );

      // Update form with city coordinates
      setFormData(prev => ({
        ...prev,
        latitude: geoResult.latitude.toString(),
        longitude: geoResult.longitude.toString(),
      }));

      setLocationInfo(geoResult);
      setSuccess(`Found: ${geoResult.displayName || `${formData.city}, ${formData.country || ''}`}`);

      // Now search for places
      await searchPlaces(geoResult.latitude, geoResult.longitude);
      setShowCategoryGrid(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  // Handle quick city selection
  const handleSearchByCityManual = async (cityName, countryName = null) => {
    setSearching(true);
    setError(null);
    setSuccess(null);

    try {
      // Get coordinates for the city
      const geoResult = await exploreService.getCityCoordinates(cityName, countryName);

      // Update form with city coordinates
      setFormData(prev => ({
        ...prev,
        city: cityName,
        country: countryName || '',
        latitude: geoResult.latitude.toString(),
        longitude: geoResult.longitude.toString(),
      }));

      setLocationInfo(geoResult);
      setSuccess(`Exploring: ${cityName}`);

      // Now search for places
      await searchPlaces(geoResult.latitude, geoResult.longitude);
      setShowCategoryGrid(false);
    } catch (err) {
      setError(`Could not find "${cityName}". Please try another city.`);
    } finally {
      setSearching(false);
    }
  };

  const searchPlaces = async (lat, lng, categoryId = null) => {
    setSearching(true);
    setError(null);

    try {
      const params = {
        latitude: lat,
        longitude: lng,
        radius: parseInt(formData.radius) || 1000,
        category: categoryId || (formData.category !== 'all' ? formData.category : null),
        limit: parseInt(formData.limit) || 10,
        query: formData.query,
      };

      const result = await exploreService.explorePlaces(params);
      setPlaces(result.places || []);
      
      if (result.places && result.places.length > 0) {
        setSuccess(`Found ${result.places.length} places!`);
      } else {
        setError('No places found. Try a different location or category.');
      }
    } catch (err) {
      setError(err.message);
      setPlaces([]);
    } finally {
      setSearching(false);
    }
  };

  const handleQuickSearch = async (categoryId) => {
    if (!formData.latitude || !formData.longitude) {
      setError('Please enter a location first');
      return;
    }

    await searchPlaces(
      parseFloat(formData.latitude),
      parseFloat(formData.longitude),
      categoryId
    );
    setShowCategoryGrid(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      switch (searchType) {
        case 'coordinates':
          const coords = exploreService.validateCoordinates(formData.latitude, formData.longitude);
          if (!coords.isValid) {
            throw new Error(coords.error || 'Please enter valid latitude and longitude');
          }
          await searchPlaces(coords.latitude, coords.longitude);
          break;
          
        case 'city':
          await handleSearchByCity();
          break;
          
        default:
          throw new Error('Invalid search type');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClear = () => {
    setPlaces([]);
    setError(null);
    setSuccess(null);
    setLocationInfo(null);
    if (selectedCategory) {
      handleBackToCategories();
    }
  };


  // Copy location to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Location details copied to clipboard!');
    } catch (err) {
      setError('Failed to copy location. Please try again.');
    }
  };


  // Handle share location - UPDATED for your data structure
const handleShareLocation = async (place) => {
  if (!place?.coordinates?.latitude || !place?.coordinates?.longitude) {
    alert('Cannot share: Location coordinates are missing');
    return;
  }
  
  const { latitude, longitude } = place.coordinates;
  
  // Use OpenStreetMap - works in Egypt and everywhere
  const openStreetMapUrl = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`;
  
  // Alternative: OpenStreetMap with name
  const osmUrlWithName = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&q=${encodeURIComponent(place.name || 'Location')}`;
  
  // Or use these URLs that work better:
  const urls = {
    osm: `https://www.openstreetmap.org/#map=15/${latitude}/${longitude}`,
    osmSearch: `https://www.openstreetmap.org/search?query=${latitude},${longitude}`,
    geoURI: `geo:${latitude},${longitude}`,
    waze: `https://www.waze.com/ul?ll=${latitude},${longitude}&navigate=yes`,
    bing: `https://www.bing.com/maps?cp=${latitude}~${longitude}&lvl=15`,
    mapquest: `https://www.mapquest.com/latlng/${latitude},${longitude}`,
    yandex: `https://yandex.com/maps/?ll=${longitude},${latitude}&z=15`,
    here: `https://wego.here.com/?map=${latitude},${longitude},15`
  };
  
  // For Egypt specifically, use OpenStreetMap
  const shareUrl = urls.osm;
  
  // Rest of your sharing logic...
  try {
    await navigator.clipboard.writeText(shareUrl);
    alert(`✅ Public link copied!\n\nUse this OpenStreetMap link for Egypt:\n${shareUrl}`);
  } catch (err) {
    alert(`📍 Share this link (works in Egypt):\n\n${shareUrl}`);
  }


  // Show share modal with the URL
  const showShareModal = () => {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
      backdrop-filter: blur(4px);
      animation: fadeIn 0.2s ease-out;
    `;
    
    modal.innerHTML = `
      <style>
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
      
      <div style="
        background: white;
        border-radius: 16px;
        padding: 24px;
        max-width: 500px;
        width: 100%;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        animation: slideUp 0.3s ease-out;
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <div>
            <h3 style="margin: 0 0 4px 0; font-size: 20px; font-weight: 600; color: #111827;">📍 Share Location</h3>
            <p style="margin: 0; font-size: 14px; color: #6b7280;">${place.name || 'Location'}</p>
          </div>
          <button onclick="this.closest('[style*=\\'position: fixed\\']').remove()" 
                  style="
                    background: none; 
                    border: none; 
                    font-size: 24px; 
                    color: #6b7280; 
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s;
                  "
                  onmouseover="this.style.background='#f3f4f6'"
                  onmouseout="this.style.background='transparent'">
            ✕
          </button>
        </div>
        
        ${place.address ? `
          <div style="background: #f8f9fa; padding: 12px; border-radius: 12px; margin-bottom: 16px;">
            <p style="margin: 0; font-size: 14px; color: #6b7280; display: flex; align-items: flex-start; gap: 8px;">
              <span style="font-size: 16px; color: #4b5563; min-width: 20px;">📍</span>
              <span>${place.address}</span>
            </p>
          </div>
        ` : ''}
        
        <div style="margin-bottom: 20px;">
          <div style="display: flex; gap: 16px; font-size: 14px; color: #6b7280;">
            <div>
              <span style="display: block; margin-bottom: 4px; font-size: 12px; color: #9ca3af;">Latitude</span>
              <span style="font-family: monospace; color: #374151;">${latitude.toFixed(6)}</span>
            </div>
            <div>
              <span style="display: block; margin-bottom: 4px; font-size: 12px; color: #9ca3af;">Longitude</span>
              <span style="font-family: monospace; color: #374151;">${longitude.toFixed(6)}</span>
            </div>
          </div>
        </div>
        
        <div style="margin-bottom: 24px;">
          <p style="margin: 0 0 8px 0; font-size: 14px; color: #6b7280;">Public share link:</p>
          <div style="display: flex; gap: 8px;">
            <input 
              type="text" 
              value="${shareUrl}" 
              readonly 
              style="
                flex: 1;
                padding: 12px;
                border: 1px solid #d1d5db;
                border-radius: 8px;
                font-size: 14px;
                background: #f9fafb;
                color: #111827;
                font-family: monospace;
              "
              id="share-url-input"
              onclick="this.select()"
            />
            <button 
              onclick="
                const input = document.getElementById('share-url-input');
                input.select();
                navigator.clipboard.writeText(input.value);
                const btn = this;
                btn.innerHTML = '<span style=\\"font-size: 16px; margin-right: 6px;\\">✓</span>Copied';
                btn.style.background = '#10b981';
                setTimeout(() => {
                  btn.innerHTML = '<span style=\\"font-size: 16px; margin-right: 6px;\\">📋</span>Copy';
                  btn.style.background = '#3b82f6';
                }, 2000);
              " 
              style="
                padding: 12px 20px;
                background: #3b82f6;
                color: white;
                border: none;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                display: flex;
                align-items: center;
                white-space: nowrap;
                transition: background 0.2s;
              "
            >
              <span style="font-size: 16px; margin-right: 6px;">📋</span>
              Copy
            </button>
          </div>
          <p style="margin: 8px 0 0 0; font-size: 12px; color: #6b7280;">
            Anyone can open this link without login or API keys
          </p>
        </div>
        
        <div style="display: flex; gap: 12px;">
          <button 
            onclick="
              window.open('${shareUrl}', '_blank');
              this.closest('[style*=\\'position: fixed\\']').remove();
            " 
            style="
              flex: 1;
              padding: 14px;
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: white;
              border: none;
              border-radius: 12px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              transition: transform 0.2s, box-shadow 0.2s;
            "
            onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 25px rgba(16, 185, 129, 0.4)'"
            onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='none'"
          >
            <span style="font-size: 18px;">🌐</span>
            Open in Google Maps
          </button>
          
          <button 
            onclick="
              const text = \`📍 ${place.name || 'Location'}\\n📍 Address: ${place.address || 'N/A'}\\n📍 Coordinates: \${latitude.toFixed(6)}, \${longitude.toFixed(6)}\\n📍 Map Link: \${shareUrl}\`;
              navigator.clipboard.writeText(text);
              const btn = this;
              btn.innerHTML = '<span style=\\"font-size: 18px; margin-right: 6px;\\">✓</span>Copied';
              btn.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
              setTimeout(() => {
                btn.innerHTML = '<span style=\\"font-size: 18px; margin-right: 6px;\\">📝</span>Text';
                btn.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
              }, 2000);
            " 
            style="
              flex: 1;
              padding: 14px;
              background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
              color: white;
              border: none;
              border-radius: 12px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              transition: transform 0.2s, box-shadow 0.2s;
            "
            onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 25px rgba(139, 92, 246, 0.4)'"
            onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='none'"
          >
            <span style="font-size: 18px;">📝</span>
            Copy as Text
          </button>
        </div>
        
        <div style="margin-top: 24px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
          <button 
            onclick="
              if (navigator.share) {
                navigator.share({
                  title: '${place.name?.replace(/'/g, "\\'") || 'Location'}',
                  text: 'Check out this location: ${place.name?.replace(/'/g, "\\'") || 'Location'} at ${latitude}, ${longitude}',
                  url: '${shareUrl}'
                });
              }
            "
            style="
              width: 100%;
              padding: 16px;
              background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
              color: white;
              border: none;
              border-radius: 12px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              transition: transform 0.2s, box-shadow 0.2s;
            "
            onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 25px rgba(245, 158, 11, 0.4)'"
            onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='none'"
          >
            <span style="font-size: 20px;">📲</span>
            Share via Device
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Close on background click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
    
    // Auto-select input text
    setTimeout(() => {
      const input = document.getElementById('share-url-input');
      if (input) {
        input.select();
        input.focus();
      }
    }, 100);
  };
  
  // Show the modal
  showShareModal();
};

  const renderCategoryCard = (category) => (
    <div 
      key={category.id}
      className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-purple-500 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      onClick={() => handleCategorySelect(category)}
    >
      <div className="text-4xl text-center mb-4">{category.icon}</div>
      <h4 className="text-lg font-semibold text-gray-800 mb-2 text-center">{category.name}</h4>
      <p className="text-sm text-gray-600 text-center">{category.description}</p>
    </div>
  );

const renderPlaceCard = (place, index) => {
  const category = categories.find(c => c.id === place.category);
  
  return (
    <>
      <div key={place.id || index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg">
        <div className="flex items-start gap-4">
          <div className="text-3xl">{category?.icon || '📍'}</div>
          <div className="flex-1">
            <h4 className="text-xl font-semibold text-gray-800">{place.name || 'Unknown Place'}</h4>
            {place.address && (
              <p className="text-gray-600 text-sm mt-1">📍 {place.address}</p>
            )}
            <div className="flex gap-4 mt-2">
              {place.distance && <span className="text-sm text-gray-500">📏 {place.distance}</span>}
              {place.rating && <span className="text-sm text-gray-500">⭐ {place.rating}</span>}
            </div>
          </div>
          {place.category && (
            <span className="bg-purple-100 text-purple-600 text-xs font-semibold px-3 py-1 rounded-full">
              {category?.name || place.category}
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button 
            onClick={() => {
              setSelectedPlace(place);
              setShowMap(true);
            }}
            className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <span>🗺️</span>
            <span>View on Map</span>
          </button>
          
          <button 
            className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-700 font-medium py-2 rounded-lg transition-colors"
            // onClick={() => handleShareLocation(place)}
          >
            📤 Share Location
          </button>
        </div>
      </div>

      {showMap && selectedPlace && selectedPlace.id === place.id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-2xl font-bold mt-12 text-gray-800">
                {selectedPlace.name} - Map View
              </h3>
              <button
                onClick={() => setShowMap(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <MapComponent
                latitude={selectedPlace.coordinates.latitude}
                longitude={selectedPlace.coordinates.longitude}
                places={[selectedPlace]}
                zoom={16}
              />
            </div>
            
            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between">
                <ShareLocationComponent 
                  latitude={selectedPlace.coordinates.latitude}
                  longitude={selectedPlace.coordinates.longitude}
                  placeName={selectedPlace.name}
                />
                <button
                  onClick={() => {
                    const url = exploreService.generateDirectionsUrl(
                      selectedPlace.coordinates.latitude,
                      selectedPlace.coordinates.longitude,
                      selectedPlace.coordinates.latitude,
                      selectedPlace.coordinates.longitude
                    );
                    window.open(url, '_blank');
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-lg"
                >
                  🗺️ Get Directions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

  if (loading && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-xl text-gray-800 font-semibold mb-2">Failed to load categories</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => initializeComponent()}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-2">Discover Places</h2>
          <p className="text-purple-100">Find restaurants, shops, services and more</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search Type Tabs */}
        <div className="bg-white rounded-2xl shadow-xl p-4 mb-8">
          <div className="flex gap-4">
            <button
              className={`flex-1 py-3 px-4 rounded-xl font-semibold ${
                searchType === 'city' 
                  ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}
              onClick={() => handleSearchTypeChange('city')}
            >
              🌆 Search by City
            </button>
            <button
              className={`flex-1 py-3 px-4 rounded-xl font-semibold ${
                searchType === 'coordinates' 
                  ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}
              onClick={() => handleSearchTypeChange('coordinates')}
            >
              📍 Enter Coordinates
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            ⚠️ {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600">
            ✅ {success}
          </div>
        )}

        {showCategoryGrid && categories.length > 0 ? (
          /* Categories Grid */
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Browse Categories</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map(category => renderCategoryCard(category))}
            </div>
          </div>
        ) : (
          /* Search Form */
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            {selectedCategory && (
              <div className="flex justify-between items-center mb-6 pb-4 border-b">
                <button
                  type="button"
                  onClick={handleBackToCategories}
                  className="text-purple-600 hover:text-purple-700"
                >
                  ← Back to Categories
                </button>
                <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-lg">
                  <span>{selectedCategory.icon}</span>
                  <span className="font-semibold text-purple-700">{selectedCategory.name}</span>
                </div>
              </div>
            )}

            {/* Location Section */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">📍 Location</h4>
              
              {searchType === 'city' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City Name *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Paris, Tokyo, New York"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country (Optional)</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="France, Japan, USA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {searchType === 'coordinates' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
                    <input
                      type="number"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      step="any"
                      placeholder="40.7128"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
                    <input
                      type="number"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      step="any"
                      placeholder="-74.0060"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Popular Cities */}
              {searchType === 'city' && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-3">Popular cities:</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_CITIES.map(city => (
                      <button
                        key={city.name}
                        type="button"
                        onClick={() => handleSearchByCityManual(city.name, city.country)}
                        className="px-3 py-1 bg-gray-100 hover:bg-purple-100 hover:text-purple-700 text-gray-700 rounded-full text-sm transition-colors"
                      >
                        {city.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Search Options */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">⚙️ Search Options</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Radius</label>
                  <select
                    name="radius"
                    value={formData.radius}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {RADIUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Results (Max: 10)</label>
                  <input
                    type="range"
                    name="limit"
                    value={formData.limit}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>10</span>
                  </div>
                </div>
                {!selectedCategory && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={searching}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-4 rounded-xl hover:shadow-lg disabled:opacity-50"
              >
                {searching ? '🔍 Searching...' : '🔍 Search Places'}
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </form>
        )}

        {/* Results Section */}
        {places.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">🎯 Found {places.length} places</h3>
              <button
                onClick={() => setShowCategoryGrid(true)}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
              >
                Browse Categories
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {places.map((place, index) => renderPlaceCard(place, index))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !showCategoryGrid && places.length === 0 && !searching && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">No places found</h4>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              onClick={handleBackToCategories}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 px-8 rounded-lg"
            >
              Browse Categories
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreComponent;

